import React, { useState, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { createMemoStyles, visuallyHidden, clsx, uniqueId, isBrowser, safeRest, isElement } from '@wds/utils';
import { useTheme } from 'react-jss';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var useStyles = createMemoStyles(function (theme) {
  var _after;

  var breakpoints = theme.breakpoints,
      motion = theme.motion,
      spacing = theme.spacing,
      palette = theme.palette,
      shadow = theme.shadow,
      type = theme.type,
      radius = theme.radius;
  var tooltip = {
    bgColor: palette.background.dark,
    typeColor: palette.text.light,
    borderRadius: radius.small,
    pipSize: 12,
    fontSize: type.fontSize.bodySm,
    padding: [spacing[4]],
    fontWeight: type.fontWeight.regular,
    boxShadow: shadow.none
  };
  var halfPipSize = tooltip.pipSize / 2;

  var tooltipTransform = function tooltipTransform(placement) {
    return {
      top: 'translate(-50%, 0)',
      bottom: "translate(-50%, ".concat(spacing[1], "px)"),
      left: 'translate(0, -50%)',
      right: 'translate(0, -50%)'
    }[placement];
  };

  var tooltipTop = function tooltipTop(placement) {
    return {
      top: 'auto',
      bottom: '100%',
      left: '50%',
      right: '50%'
    }[placement];
  };

  var tooltipBottom = function tooltipBottom(placement) {
    return {
      top: '100%',
      bottom: 'auto',
      left: 'auto',
      right: 'auto'
    }[placement];
  };

  var tooltipLeft = function tooltipLeft(placement) {
    return {
      top: '50%',
      bottom: '50%',
      left: 'auto',
      right: '100%'
    }[placement];
  };

  var tooltipAlign = function tooltipAlign(placement) {
    return {
      top: 'center',
      bottom: 'center',
      left: 'right',
      right: 'left'
    }[placement];
  };

  var tooltipBodyLeft = function tooltipBodyLeft(placement) {
    return {
      top: '50%',
      bottom: '50%',
      left: 'auto',
      right: -halfPipSize
    }[placement];
  };

  var tooltipBodyTop = function tooltipBodyTop(placement) {
    return {
      top: 'auto',
      bottom: -halfPipSize,
      left: '50%',
      right: '50%'
    }[placement];
  };

  return {
    wrenchTooltipContainer: function wrenchTooltipContainer(_ref) {
      var stretched = _ref.stretched;
      return _objectSpread$1({
        position: 'relative',
        outline: 'none',
        boxSizing: 'border-box',
        padding: 0,
        margin: 0
      }, stretched && {
        display: 'block',
        width: '100%'
      });
    },
    wrenchTooltip: _defineProperty({
      boxSizing: 'border-box',
      padding: 0,
      margin: 0,
      zIndex: function zIndex(_ref2) {
        var elevation = _ref2.elevation;
        return elevation || theme.elevation.sky;
      },
      position: 'absolute',
      width: 300,
      outline: 'none',
      pointerEvents: 'none',
      whiteSpace: 'normal',
      opacity: function opacity(_ref3) {
        var show = _ref3.show;
        return show ? 1 : 0;
      },
      left: '50%',
      top: function top(_ref4) {
        var placement = _ref4.placement;
        return placement === 'bottom' ? '100%' : 'auto';
      },
      bottom: function bottom(_ref5) {
        var placement = _ref5.placement;
        return placement === 'bottom' ? 'auto' : '100%';
      },
      marginTop: function marginTop(_ref6) {
        var placement = _ref6.placement;
        return placement === 'bottom' ? tooltip.pipsize : 0;
      },
      transform: function transform(_ref7) {
        var show = _ref7.show,
            placement = _ref7.placement;

        if (show) {
          if (placement === 'bottom') {
            return "translate(-50%, ".concat(spacing[1], "px)");
          }

          return 'translate(-50%, 0)';
        }

        return "translate(-50%, -".concat(spacing[1], "px)");
      },
      textAlign: 'center',
      marginBottom: tooltip.pipSize,
      // delay
      transitionDelay: function transitionDelay(_ref8) {
        var delay = _ref8.delay;
        return delay ? '1000ms' : 'none';
      },
      // show transition
      transition: function transition(_ref9) {
        var show = _ref9.show;
        return show ? "transform 250ms ".concat(motion.curve.enter, ", opacity 50ms linear 50ms") : 'all 0s ease 0s';
      }
    }, "@media (min-width: ".concat(breakpoints.md, "px)"), {
      transform: function transform(_ref10) {
        var placement = _ref10.placement;
        return tooltipTransform(placement);
      },
      top: function top(_ref11) {
        var placement = _ref11.placement;
        return tooltipTop(placement);
      },
      bottom: function bottom(_ref12) {
        var placement = _ref12.placement;
        return tooltipBottom(placement);
      },
      left: function left(_ref13) {
        var placement = _ref13.placement;
        return tooltipLeft(placement);
      },
      right: function right(_ref14) {
        var placement = _ref14.placement;
        return placement === 'left' ? '100%' : 'auto';
      },

      /**
       * TODO: Need to make pipSize a rem value if we want to define in REM
       */
      marginLeft: function marginLeft(_ref15) {
        var placement = _ref15.placement;
        return placement === 'right' ? spacing[4] : 0;
      },
      // tooltip.pipSize + spacing[1]
      marginRight: function marginRight(_ref16) {
        var placement = _ref16.placement;
        return placement === 'left' ? spacing[4] : 0;
      },
      // tooltip.pipSize + spacing[1]
      textAlign: function textAlign(_ref17) {
        var placement = _ref17.placement;
        return tooltipAlign(placement);
      }
    }),
    wrenchTooltipBody: {
      fontFamily: type.fontFamily.base,
      '-webkit-font-smoothing': 'antialiased',
      display: 'inline-block',
      borderRadius: tooltip.borderRadius,
      background: tooltip.bgColor,
      color: tooltip.typeColor,
      fontSize: tooltip.fontSize,
      padding: tooltip.padding,
      lineHeight: 1.5,
      textAlign: 'left',
      fontWeight: tooltip.fontWeight,
      boxShadow: tooltip.boxShadow,
      '&:after': (_after = {
        bottom: function bottom(_ref18) {
          var placement = _ref18.placement;
          return placement === 'bottom' ? 'auto' : -halfPipSize;
        },
        left: '50%',
        marginLeft: -halfPipSize,
        content: '""',
        position: 'absolute',
        width: tooltip.pipSize,
        height: tooltip.pipSize,
        backgroundColor: tooltip.bgColor,
        transform: 'rotate(45deg)'
      }, _defineProperty(_after, "@media (min-width: ".concat(breakpoints.md, "px)"), {
        top: function top(_ref19) {
          var placement = _ref19.placement;
          return tooltipBodyTop(placement);
        },
        left: function left(_ref20) {
          var placement = _ref20.placement;
          return tooltipBodyLeft(placement);
        },
        right: function right(_ref21) {
          var placement = _ref21.placement;
          return placement === 'left' ? -halfPipSize : 'auto';
        },
        marginTop: function marginTop(_ref22) {
          var placement = _ref22.placement;
          return placement === 'right' || placement === 'left' ? -halfPipSize : 0;
        },
        marginLeft: function marginLeft(_ref23) {
          var placement = _ref23.placement;
          return placement === 'right' ? 0 : -halfPipSize;
        }
      }), _defineProperty(_after, "@media (max-width: ".concat(breakpoints.md, "px)"), {
        top: function top(_ref24) {
          var placement = _ref24.placement;
          return placement === 'bottom' ? -halfPipSize : 'auto';
        }
      }), _after)
    },
    wrenchTooltipBodyCaret: function wrenchTooltipBodyCaret(_ref25) {
      var hideCaret = _ref25.hideCaret;
      return {
        '&:after': _objectSpread$1({}, hideCaret && visuallyHidden)
      };
    }
  };
});
var _excluded = ["delay", "hideCaret", "placement", "text", "show", "disableHover", "renderTo", "onChange", "alwaysShow", "children", "elevation"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var PLACEMENTS = ["bottom", "left", "right", "top"];

var Tooltip = function Tooltip(_ref) {
  var delay = _ref.delay,
      hideCaret = _ref.hideCaret,
      placement = _ref.placement,
      text = _ref.text,
      propShow = _ref.show,
      disableHover = _ref.disableHover,
      renderTo = _ref.renderTo,
      onChange = _ref.onChange,
      alwaysShow = _ref.alwaysShow,
      children = _ref.children,
      elevation = _ref.elevation,
      rest = _objectWithoutProperties(_ref, _excluded); // states


  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      show = _useState2[0],
      setShow = _useState2[1];

  var _useState3 = useState({}),
      _useState4 = _slicedToArray(_useState3, 2),
      tooltipStyle = _useState4[0],
      setTooltipStyle = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      stretched = _useState6[0],
      setStretched = _useState6[1]; // useTheme and Styles


  var theme = useTheme();
  var classes = useStyles({
    theme: theme,
    show: propShow || show || alwaysShow,
    placement: placement,
    disableHover: disableHover,
    hideCaret: hideCaret,
    stretched: stretched,
    delay: delay,
    elevation: elevation
  });
  var tooltipBodyClasses = clsx(classes.wrenchTooltipBody, classes.wrenchTooltipBodyCaret); // Refs

  var tooltipRef = useRef(null);
  var tooltipContentRef = useRef(null);
  var uniqueTooltipName = useMemo(function () {
    return uniqueId('wds-tooltip-');
  }, []); // Set to 800 by default

  var TOOLTIP_TIMEOUT;

  var updatePosition = function updatePosition() {
    if (tooltipRef.current && show && tooltipContentRef.current.children && tooltipContentRef.current.children.length) {
      var rect = tooltipRef.current.getBoundingClientRect();
      var childRect = tooltipContentRef.current.children[0].getBoundingClientRect();
      var bottom = 'auto';
      var pipSize = placement === 'top' ? theme.spacing[2] : theme.spacing[4];
      var screenPlacement = window.innerWidth <= theme.breakpoints.md && (placement === PLACEMENTS[2] || placement === PLACEMENTS[3]) ? PLACEMENTS[0] : placement;
      var position = {
        top: {
          top: rect.top - childRect.height - rect.height / 2 - pipSize,
          left: rect.left + rect.width / 2
        },
        left: {
          top: rect.top + rect.height / 2,
          left: rect.left - childRect.width * 2
        },
        right: {
          top: rect.top + rect.height / 2,
          left: rect.left + rect.width
        }
      }[screenPlacement] || {
        top: rect.bottom,
        left: rect.left + rect.width / 2
      };
      var z = (elevation || theme.elevation.space) + 1;
      setTooltipStyle({
        top: position.top,
        left: position.left,
        zIndex: z,
        position: 'fixed',
        bottom: bottom
      });
    }
  };

  var change = function change(value) {
    if (renderTo && value) {
      updatePosition();
    }

    if (onChange) {
      onChange(value);
    }
  };

  var showTooltip = function showTooltip(val) {
    setShow(val);
    change(val);
  };

  var handleScroll = function handleScroll() {
    if (show && renderTo) {
      updatePosition();
    }
  };

  var handleClick = function handleClick(e) {
    if (!alwaysShow) {
      if (children.props.onClick) children.props.onClick(e);
      showTooltip(!show);
    }
  }; // componentDidMount


  useEffect(function () {
    if (show && renderTo) {
      updatePosition();
    }

    if (isBrowser && renderTo && show) {
      updatePosition();
    }

    if (disableHover && isBrowser) {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      window.addEventListener('orientationchange', handleScroll);
    } // componentWillUnmount


    return function () {
      if (disableHover && renderTo && isBrowser) {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        window.removeEventListener('orientationchange', handleScroll);
      }

      if (delay) {
        clearTimeout(TOOLTIP_TIMEOUT);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  var onMouseEnter = function onMouseEnter() {
    if (!disableHover && !alwaysShow) {
      if (delay) {
        TOOLTIP_TIMEOUT = setTimeout(function () {
          setShow(true);
          change(true);
        }, 800);
        return;
      }

      setShow(true);
      change(true);
    }
  };

  var onMouseLeave = function onMouseLeave() {
    if (delay) {
      clearTimeout(TOOLTIP_TIMEOUT);
    }

    if (!disableHover && !alwaysShow) {
      setShow(false);
      change(false);
    }
  };

  var renderTooltipContent = function renderTooltipContent() {
    var tooltipContent = /*#__PURE__*/React.createElement("span", {
      className: classes.wrenchTooltip,
      id: uniqueTooltipName,
      ref: tooltipContentRef,
      role: "tooltip",
      style: tooltipStyle
    }, /*#__PURE__*/React.createElement("span", {
      className: tooltipBodyClasses
    }, text));

    if (isElement(renderTo) || renderTo && renderTo.current) {
      return /*#__PURE__*/createPortal(tooltipContent, isElement(renderTo) ? renderTo : renderTo.current);
    }

    return tooltipContent;
  };

  var newChildren = React.Children.map(children, function (child) {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }

    var type = child.type,
        childProps = child.props;

    if (childProps && childProps.stretched !== stretched) {
      setStretched(childProps.stretched);
    }

    var displayName = type.displayName;

    var additionalProps = _objectSpread(_objectSpread(_objectSpread({}, displayName && displayName !== 'Button' && {
      tabIndex: 0
    }), displayName ? {
      describedby: uniqueTooltipName
    } : {
      'aria-describedby': uniqueTooltipName
    }), {}, {
      onClick: !disableHover && childProps.onClick ? childProps.onClick : handleClick
    });

    return /*#__PURE__*/React.cloneElement(child, _objectSpread({}, additionalProps));
  });
  return /*#__PURE__*/React.createElement("span", Object.assign({
    className: classes.wrenchTooltipContainer
  }, safeRest(rest)), renderTooltipContent(), /*#__PURE__*/React.createElement("span", {
    className: classes.wrenchTooltipTrigger,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onFocus: onMouseEnter,
    onBlur: onMouseLeave,
    ref: tooltipRef
  }, newChildren));
};

Tooltip.displayName = "Tooltip";
Tooltip.propTypes = {
  /**
   * Tooltip wraps around any component that requires it
   */
  children: PropTypes.node,

  /**
   * Add a short entrance delay before summoning the tooltip when you hover a trigger. The delay is 1s.
   */
  delay: PropTypes.bool,

  /**
   * You can remove the caret from the tooltip for instances
   * when showing context of which object summoned it is not necessary.
   */
  hideCaret: PropTypes.bool,

  /**
   * Placement variants, they will adjust according to screen size.
   * Left & Right placement will be adjusted to top or bottom if your screen size cannot
   * adequately display them
   */
  placement: PropTypes.oneOf(PLACEMENTS),

  /**
   * The content for the tooltip
   */
  text: PropTypes.node.isRequired,

  /**
   * If true, Tooltip will display in current view
   */
  show: PropTypes.bool,

  /**
   * If true, tooltip must be triggered by click handler.
   * Hovering over the child component will no longer work
   */
  disableHover: PropTypes.bool,

  /**
   * If your tooltip is within a container which hides overflowing content such as a modal or card,
   * you may want to render the tooltip attached to a parent element so that it can be rendered outside of
   * the trigger container. The tooltip component will handle positioning the popout relative to the trigger element.
   */
  renderTo: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.elementType
  })]),

  /**
   * onChange handler
   * function(event: object) => void
   */
  onChange: PropTypes.func,

  /**
   * If true, always display the tooltip
   */
  alwaysShow: PropTypes.bool,

  /**
   * If provided, overrides the z-index value of the component
   */
  elevation: PropTypes.number
};
Tooltip.defaultProps = {
  children: undefined,
  delay: false,
  hideCaret: false,
  placement: 'top',
  show: false,
  disableHover: false,
  renderTo: undefined,
  onChange: function onChange() {
    return undefined;
  },
  alwaysShow: undefined,
  elevation: undefined
};
export { Tooltip };